import React, { useEffect, useState } from 'react';
import { TextField, Button, List, Grid2 as Grid, Typography, Avatar, Divider, MenuItem, Menu, IconButton, Box, useTheme } from '@mui/material';
import { formatDate } from '../../Utils/Utils';
import { MoreVert } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { Comment, DataTypes } from '../../Types/UnitFeatureTypes';
import { Actions } from '../../Redux/Reducer/rootReducer';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/authContext';

interface CommentSectionProps {
  previousComments: Comment[];
}

const CommentSection: React.FC<CommentSectionProps> = ({ previousComments }) => {
  const [comment, setComment] = useState<string>('');
  const dispatch = useDispatch();
  const location = useLocation();
  const [comments, setComments] = useState<Comment[]>([...previousComments]);
  const theme = useTheme();
  const {user} = useAuth();

  useEffect(() => {
    setComments(previousComments);
  },[previousComments])

  // Handle comment submission
  const handleCommentSubmit = () => {
    if (comment.trim() === '') return;

    const commentsData: Comment = {
      comment: comment.trim(),
      resource_id: location.state.id || -1,
      commenter_id: user.id,
      user: {
        first_name: user.first_name,
        last_name: user.last_name,
        avatar: user.avatar,
        id: user.id
      }
    };

    setComments((prevComments) => [commentsData, ...prevComments]);
    setComment('');

    switch (location.state.type) {
      case DataTypes.BOOKING:
        dispatch(Actions.booking.createBookingCommentRequestAction.request({
          comment: commentsData
        }));
        break;
      case DataTypes.VIOLATION:
        dispatch(Actions.violation.createViolationCommentRequestAction.request({
          comment: commentsData
        }));
        break;
      case DataTypes.SERVICE_REQUEST:
        dispatch(Actions.serviceRequestUpdates.addServiceRequestUpdateAction.request({
          serviceRequestUpdateData: commentsData
        }));
        break;
      default:
        break;
    }
  };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteComment = (commentId: number) => {
        setComments((prevComments) => prevComments.filter(comment => comment.id !== commentId));
        handleClose();
    };
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <TextField
            fullWidth
            variant="standard"
            name="comment"
            margin='normal'
            placeholder='Add an update...'
            multiline
            rows={4}
            value={comment}
            slotProps={{
            input: {
                disableUnderline: true,
                style: {
                  fontSize: '1.1rem'
                }
            },
            }}
            sx={{
              borderRadius: 3,
              border: `0.5px ridge ${theme.palette.primary.main}`,
              padding: 2,
            '&:hover': {
                backgroundColor: 'grey.100',
            }
            }}
            onChange={(e) => setComment(e.target.value)}
        />
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button 
            variant="contained" 
            color="primary" 
            onClick={handleCommentSubmit} 
            disabled={!comment.trim()}
        >
            Post Update
        </Button>
        </Box>

      {comments.length > 0 && <List sx={{mt: 4}}>
        {comments.map((comment) => (
            <>
              <Grid container sx={{display: 'flex', alignItems: 'center', ml: 4}}>
                  <Grid size={{xs: 2.5, lg: 1}}>
                    <Avatar
                      sx={{ bgcolor: 'primary.main', color: 'primary.contrastText'}}
                      src={comment.user?.avatar} />
                  </Grid>
                  <Grid size={{xs: 8.5, lg: 10}}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold'}}>
                      {comment.user?.first_name} {comment.user?.last_name}
                      </Typography>
                      <Typography variant="subtitle2" fontSize={11} color="text.secondary">
                      {comment.created_on ? formatDate(comment.created_on) : 'Date not available'}
                      </Typography>
                  </Grid> 
                  <Grid size={{xs: 1, lg: 1}}>
                    {comment.commenter_id === -1 && <IconButton
                        onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        }}
                  > <MoreVert sx={{fontSize: 20}} /></IconButton>}
                  <Menu
                      id="simple-menu"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                  >
                      <MenuItem onClick={() => comment.id !== undefined && handleDeleteComment(comment.id)}>Delete</MenuItem>
                  </Menu>
                  </Grid>
              </Grid>
              <Grid container sx={{ml: 4}}>
                  <Grid size={{xs: 2.5, lg: 1}}></Grid>
                  <Grid size={{xs: 8.5, lg: 11}}>
                      <Typography variant="body1">
                      {comment.comment}
                      </Typography>
                  </Grid> 
              </Grid>
              <Divider sx={{my: 1}} />
              </>
        ))}
      </List>}
    </>
  );
};

export default CommentSection;
